import { FunctionComponent, useEffect } from 'react';

// Types
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

// Components
import Head from 'next/head';
import { NextSeo } from 'next-seo';
import Degree from '@Pages/tra-cuu/bac-si/Components/Degree';
import Comment from '@Pages/tra-cuu/bac-si/Components/Comment';
import Introduce from '@Pages/tra-cuu/bac-si/Components/Introduce';
import Specialist from '@Pages/tra-cuu/bac-si/Components/Specialist';
import DoctorInfo from '@Pages/tra-cuu/bac-si/Components/DoctorInfo';
import { PageView } from '@Components/Utils/Lazy/Script';
import ButtonAdvisory from '@Pages/tra-cuu/bac-si/Components/ButtonAdvisory';
import LayoutContainer from '@Components/Layout/Components/Container';
import Breadcrumb, { PathLevelProps } from '@Components/UI/Breadcrumb';

import { DataManager } from '@Components/Utils/DataTransfer';

// Utils
import classnames from 'classnames';
import { getServerSideHttp } from '@Libs/http';

// Contexts
import { useAccountContext } from '@Components/Account/Contexts';
import { useViewDetected, ViewMedia } from '@Components/Utils/ViewDetected';

// Styles
import styles from './DoctorDetail.module.scss';

// Api
import doctorApi, { useDoctor } from '@Libs/api/user/doctor';

// Type
import { Doctor } from './DoctorPage';
import { NextSeoProps } from 'next-seo';
import { openApp } from '@Libs/utils';

interface DoctorDetailProps {
    doctor: Doctor;
}

const DoctorDetail: FunctionComponent<InferGetServerSidePropsType<typeof getServerSideProps> & DoctorDetailProps> = (props) => {
    const { summary } = useAccountContext();
    const { deviceType } = useViewDetected();

    const {
        mutate,
        data: { doctor },
    } = useDoctor(props?.doctor?.id, {
        initialData: {
            doctor: props.doctor,
        },
    });

    openApp(`edoctor://doctor_detail/${props.doctor?.id}`);

    useEffect(() => {
        mutate();
    }, [summary]);

    useEffect(() => {
        if (deviceType !== 'desktop') {
            document.body.classList.add(styles.body_padding);
        }

        return () => {
            document.body.classList.remove(styles.body_padding);
        };
    }, []);

    const paths: Array<PathLevelProps> = [
        {
            label: 'Tra cứu',
            path: '/tra-cuu',
        },
        {
            label: `Bác sĩ ${doctor?.profile?.fullName}`,
        },
    ];

    const education = doctor?.profile?.education;
    const certificate = doctor?.profile?.certification;

    const bodyClass = classnames([
        styles.body,
        {
            [styles.mobile]: ['mobile', 'tablet'].includes(deviceType),
        },
    ]);

    const title = `eDoctor: Bác sĩ ${doctor?.profile?.fullName}`;
    const seo: NextSeoProps = {
        title,
        description: doctor.about,
        openGraph: {
            title,
            type: 'website',
            description: doctor.about,
        },
    };

    return (
        <>
            <Head>
                <meta property="al:ios:url" content={`edoctor://doctor_detail/${props.doctor.id}`} />
                <meta property="al:android:url" content={`edoctor://doctor_detail/${props.doctor.id}`} />
            </Head>

            <NextSeo {...seo} />

            <DataManager {...{ doctor }}>
                <div className={styles.wrapper}>
                    <LayoutContainer>
                        <Breadcrumb paths={paths} className={styles.breadcrumb} />
                    </LayoutContainer>
                    <LayoutContainer className={bodyClass}>
                        <div className={styles.left_info}>
                            <DoctorInfo className={styles.doctor_info} />
                        </div>
                        <div className={styles.right_info}>
                            <div className={styles.info_item}>
                                <Introduce />
                            </div>

                            <div className={styles.info_item}>
                                <Specialist />
                            </div>

                            {education?.length > 0 && (
                                <div className={styles.info_item}>
                                    <Degree title="Bằng cấp" degreeData={education} icon="/assets/tra-cuu/bac-si/book_icon.png" />
                                </div>
                            )}

                            {certificate?.length > 0 && (
                                <div className={styles.info_item}>
                                    <Degree title="Chứng chỉ" degreeData={certificate} icon="/assets/tra-cuu/bac-si/medal_white_icon.png" />
                                </div>
                            )}

                            <div className={styles.info_item}>
                                <Comment />
                            </div>
                        </div>
                    </LayoutContainer>
                    <ViewMedia is={['mobile', 'tablet']}>
                        <ButtonAdvisory className={styles.btn_advisory} />
                    </ViewMedia>
                </div>

                <PageView />
            </DataManager>
        </>
    );
};

export default DoctorDetail;

export const getServerSideProps: GetServerSideProps = async (context) => {
    const http = getServerSideHttp(context);

    const id: any = context.params?.id || '';
    if (!context.params?.id) {
        return {
            notFound: true,
        };
    }
    // const response = await doctorApi(http).getDoctorId(id);
    const response = await(async () => {
        try {
            return await doctorApi(http).getDoctorId(id);
        } catch (error) {
            return {};
        }
    })();

    const doctor = response.doctor;

    return {
        props: {
            doctor,
        },
        ...(!doctor && { notFound: true }),
    };
};
